import React, { FC, ReactNode } from 'react';

import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material';
import parse from 'html-react-parser';

import AnimatedSkeleton from '~/components/AnimatedSkeleton';
import AnimatedSkeletonMultiline from '~/components/AnimatedSkeletonMultiline';
import ImpactsBlock from '~/components/ImpactsBlock/ImpactsBlock';
import SkillsBlock from '~/components/SkillsBlock/SkillsBlock';
import useTenantTranslation from '~/hooks/useTenantTranslation';
import { PAYWALLED_CONTENT_SELECTORS } from '~/pages/SingleVideoPage/const';
import {
  MIDDLE_MARGIN_PX,
  SKELETON_TEXT_HEIGHT_PX,
  SKELETON_TEXT_HEIGHT_SMALL_PX,
  SMALL_MARGIN_PX,
  SUBMIDDLE_MARGIN_PX,
  TINY_MARGIN_PX,
} from '~/theme';
import { Impact, KeyLearningObjective, Maybe, Skill } from '~/types';
import TypographyWithTerms from '~/ui/components/TextWithTerms/TypographyWithTerms';

interface OverviewProps {
  summary?: string | null;
  keyLearningObjectives?: (KeyLearningObjective | null)[] | null | undefined;
  loading?: boolean;
  skills?: Maybe<Maybe<Skill>[]>;
  impacts?: Maybe<Maybe<Impact>[]>;
  children?: ReactNode;
}

const Overview: FC<OverviewProps> = ({
  summary,
  keyLearningObjectives,
  loading,
  skills,
  impacts,
  children,
}) => {
  const { t } = useTenantTranslation();

  const skillsExist = skills && skills?.length > 0;
  const impactsExist = impacts && impacts?.length > 0;

  if (loading) {
    return (
      <Box>
        <LoadingObjectivesTitle xs={SKELETON_TEXT_HEIGHT_PX} />
        <LoadingDescription xs={SKELETON_TEXT_HEIGHT_SMALL_PX} lines={5} spacer={TINY_MARGIN_PX} />
        <LoadingObjectivesTitle xs={SKELETON_TEXT_HEIGHT_PX} />
        <LoadingDescription
          xs={SKELETON_TEXT_HEIGHT_SMALL_PX}
          lines={2}
          spacer={TINY_MARGIN_PX}
          lastLineWidth={70}
        />
      </Box>
    );
  }

  return (
    <Box className={PAYWALLED_CONTENT_SELECTORS.OVERVIEW}>
      {keyLearningObjectives && keyLearningObjectives.length > 0 && (
        <>
          <GeneratedTabHeader>{t('page.video.keyLearningObj')}:</GeneratedTabHeader>
          <GeneratedList data-testid="keyLearningObjectives">
            {keyLearningObjectives.map((item) => (
              <GeneratedListItem key={item?.id}>
                <GeneratedListText>{item?.title}</GeneratedListText>
              </GeneratedListItem>
            ))}
          </GeneratedList>
        </>
      )}
      {(skillsExist || impactsExist) && (
        <SkillsImpactsWrapper>
          {skillsExist && <SkillsBlock skills={skills} />}
          {impactsExist && <ImpactsBlock impacts={impacts} />}
        </SkillsImpactsWrapper>
      )}
      {summary && (
        <>
          <GeneratedTabHeader>{t('page.video.overview')}:</GeneratedTabHeader>
          <GeneratedSummaryWrapper data-testid="overviewSummary">
            <GeneratedTabText>{parse(summary)}</GeneratedTabText>
          </GeneratedSummaryWrapper>
        </>
      )}
      {children}
    </Box>
  );
};

export default Overview;

const GeneratedSummaryWrapper = styled(Box)`
  margin-bottom: ${MIDDLE_MARGIN_PX};
`;
const GeneratedTabText = styled(TypographyWithTerms)`
  font-size: 16px;
  line-height: 1.5;
  color: ${({ theme }) => theme.palette.common.text.secondaryToPrimary};
  white-space: pre-wrap;

  & h1,
  & h2,
  & h3,
  & h4,
  & h5,
  & h6,
  & p,
  & ul,
  & ol {
    margin-bottom: 18px;
    margin-top: 0;
  }

  & h1,
  & h2,
  & h3,
  & h4,
  & h5,
  & h6 {
    color: ${({ theme }) => theme.palette.common.white};
    line-height: 1.5;
  }

  & ul,
  & ol {
    padding-left: 20px;

    & li {
      &::marker {
        color: ${({ theme }) => theme.palette.common.primary};
      }
    }
  }

  & svg g {
    fill: ${({ theme }) => theme.palette.common.white};
    stroke: ${({ theme }) => theme.palette.common.white};
  }

  ${({ theme }) => theme.breakpoints.up('md')} {
    font-size: 18px;
    margin-bottom: ${MIDDLE_MARGIN_PX};
  }
`;
const GeneratedTabHeader = styled(Typography)`
  font-size: 16px;
  line-height: 1;
  margin-bottom: ${TINY_MARGIN_PX};
  font-weight: bold;

  ${({ theme }) => theme.breakpoints.up('md')} {
    font-size: 18px;
  }
`;
const GeneratedList = styled('ul')`
  padding-left: 20px;
  margin: 0 0 18px;
`;
const GeneratedListItem = styled('li')`
  &::marker {
    color: ${({ theme }) => theme.palette.common.primary};
  }
`;
const GeneratedListText = styled(Typography)`
  font-size: 16px;
  line-height: 1.5;
  color: ${({ theme }) => theme.palette.common.text.secondaryToPrimary};

  ${({ theme }) => theme.breakpoints.up('md')} {
    font-size: 18px;
  }
`;
const LoadingObjectivesTitle = styled(AnimatedSkeleton)`
  max-width: 300px;
  margin-bottom: ${SMALL_MARGIN_PX};
`;
const LoadingDescription = styled(AnimatedSkeletonMultiline)`
  margin-bottom: ${MIDDLE_MARGIN_PX};
`;
const SkillsImpactsWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  gap: ${SMALL_MARGIN_PX} ${SUBMIDDLE_MARGIN_PX};
  margin-bottom: ${SMALL_MARGIN_PX};

  ${({ theme }) => theme.breakpoints.up('md')} {
    align-items: center;
    flex-direction: row;
    gap: ${MIDDLE_MARGIN_PX};
  }
`;
